import { Input, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { Search16Regular } from "@fluentui/react-icons";
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { HELP_Z } from "./dom";
import type { HelpLanguage, HelpStep, PageHelp } from "./types";
import { groupStepsBySection, localizedSection, localizedStep, stepMatchesQuery } from "./types";

/**
 * "Jump to a step" — the table of contents behind the tour's step counter. Someone who wants the
 * one field they do not understand should not press Next forty times to reach it, and a user who
 * cannot name the field needs to search the prose, not scan titles — hence `stepMatchesQuery`.
 *
 * Grouped by the guide's sections, because the sections are how the page is laid out.
 *
 * A plain fixed panel rather than a Fluent `Popover`: it lives inside the tour's own layer, must
 * sit above the tour card, and its Escape handling belongs to the tour's capture-phase listener.
 */

const PANEL_WIDTH = 340;
const LIST_MAX_HEIGHT = 340;
/** Search row + list + footer, for keeping the panel inside the window. */
const PANEL_MAX_HEIGHT = LIST_MAX_HEIGHT + 90;

const useStyles = makeStyles({
  panel: {
    position: "fixed",
    width: `${PANEL_WIDTH}px`,
    zIndex: HELP_Z.jump,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius("16px"),
    boxShadow: tokens.shadow28,
    textAlign: "left",
    lineHeight: "normal",
  },
  searchWrap: {
    ...shorthands.padding("10px", "10px", "6px"),
  },
  search: {
    width: "100%",
  },
  list: {
    maxHeight: `${LIST_MAX_HEIGHT}px`,
    overflowY: "auto",
    ...shorthands.padding("0", "10px", "10px"),
  },
  empty: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding("12px", "4px"),
  },
  group: {
    fontSize: "9.5px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding("0", "6px"),
    marginTop: "8px",
    marginBottom: "2px",
  },
  row: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "8px",
    textAlign: "left",
    ...shorthands.padding("5px", "6px"),
    ...shorthands.border("none"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    fontFamily: "inherit",
    ":hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
  },
  rowCurrent: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  rowTitle: {
    fontSize: "12.5px",
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    overflowY: "hidden",
    textOverflow: "ellipsis",
  },
  rowTitleCurrent: {
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
  },
  rowNumber: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  footer: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding("6px", "16px"),
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
  },
});

/** One line of the table of contents, memoised: the list is rebuilt on every keystroke. */
const JumpRow = memo(
  ({
    step,
    language,
    index,
    current,
    onPick,
  }: {
    step: HelpStep;
    language: HelpLanguage;
    /** The step's place in the tour — the number the reader sees, and what `onPick` reports. */
    index: number;
    current: boolean;
    onPick: (index: number) => void;
  }) => {
    const styles = useStyles();
    return (
      <button
        type="button"
        onClick={() => onPick(index)}
        aria-current={current ? "step" : undefined}
        className={mergeClasses(styles.row, current && styles.rowCurrent)}
      >
        <span className={mergeClasses(styles.rowTitle, current && styles.rowTitleCurrent)}>
          {localizedStep(step, language).title}
        </span>
        <span className={styles.rowNumber}>{index + 1}</span>
      </button>
    );
  }
);
JumpRow.displayName = "JumpRow";

interface HelpStepJumpProps {
  /** The tour's step counter — the panel opens under it. */
  anchor: HTMLElement | null;
  /** The tour's steps, in tour order — indices into this array are what `onPick` returns. */
  steps: HelpStep[];
  sections?: string[];
  help: PageHelp;
  language: HelpLanguage;
  currentIndex: number;
  onPick: (index: number) => void;
  onClose: () => void;
}

export const HelpStepJump = ({
  anchor,
  steps,
  sections,
  help,
  language,
  currentIndex,
  onPick,
  onClose,
}: HelpStepJumpProps) => {
  const styles = useStyles();
  const panelRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  /**
   * The list may lag a frame behind the typing; the field may not. Enter is resolved against
   * the LIVE query — a jump to the previous keystroke's first match is the wrong step.
   */
  const deferredQuery = useDeferredValue(query);

  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery]
  );
  const groups = useMemo(() => groupStepsBySection(sections, matches), [sections, matches]);
  /** Tour position by selector, built once per tour rather than by `indexOf` per row. */
  const positionOf = useMemo(
    () => new Map(steps.map((step, index) => [step.selector, index])),
    [steps]
  );

  const pick = useCallback(
    (index: number): void => {
      onPick(index);
      setQuery("");
      onClose();
    },
    [onPick, onClose]
  );

  // A press anywhere else on the tour card closes the list, like a popover would. Not the
  // counter (it toggles the list itself) and not the tour's backdrop, whose own click closes
  // the list — closing it here first would let that click end the whole tour instead.
  useEffect(() => {
    const onDown = (event: PointerEvent): void => {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target) || anchor?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest('[data-help-panel="tour-blocker"], [data-help-panel="tour-spotlight"]')
      ) {
        return;
      }
      onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => window.removeEventListener("pointerdown", onDown, true);
  }, [anchor, onClose]);

  const anchorRect = anchor?.getBoundingClientRect();
  // Below the counter, but never off the bottom of a short window.
  const top = Math.max(
    12,
    Math.min(anchorRect ? anchorRect.bottom + 4 : 80, window.innerHeight - PANEL_MAX_HEIGHT - 12)
  );
  const left = anchorRect
    ? Math.min(Math.max(12, anchorRect.left), window.innerWidth - PANEL_WIDTH - 12)
    : 12;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Jump to a step"
      // The guide's own surface: never the dialog a tour confines itself to.
      data-help-panel="jump"
      className={styles.panel}
      style={{ top, left }}
    >
      <div className={styles.searchWrap}>
        <Input
          className={styles.search}
          size="small"
          autoFocus
          value={query}
          onChange={(_event, data) => setQuery(data.value)}
          contentBefore={<Search16Regular />}
          maxLength={60}
          aria-label="Search the steps on this page"
          placeholder="Search this page’s guide…"
          data-help="help-jump-search"
          onKeyDown={(event) => {
            // Enter goes to the best match, so a known field is two keystrokes away.
            if (event.key !== "Enter") return;
            const best = steps.findIndex((step) => stepMatchesQuery(step, query));
            if (best < 0) return;
            event.preventDefault();
            pick(best);
          }}
        />
      </div>

      <div className={styles.list}>
        {matches.length === 0 && (
          <div className={styles.empty}>
            Nothing on this screen matches. The panel’s guide lists the parts that are not on
            screen right now too.
          </div>
        )}

        {groups.map((group) => (
          <div key={group.section}>
            <div className={styles.group}>{localizedSection(help, group.section, language)}</div>
            {group.steps.map((step) => {
              const index = positionOf.get(step.selector);
              // Every grouped step came out of `steps`, so this holds; rendering nothing keeps an
              // impossible case from becoming a row that jumps to step 1.
              if (index === undefined) return null;
              return (
                <JumpRow
                  key={step.selector}
                  step={step}
                  language={language}
                  index={index}
                  current={index === currentIndex}
                  onPick={pick}
                />
              );
            })}
          </div>
        ))}
      </div>

      {deferredQuery.length > 0 && matches.length > 0 && (
        <div className={styles.footer}>
          {matches.length} of {steps.length} · Enter goes to the first
        </div>
      )}
    </div>
  );
};
